import { Injectable, inject } from '@angular/core';
import { Observable, Subject, Subscriber, catchError, from, map, of, switchMap, tap } from 'rxjs';
import { browserRenderableExtensions } from '../consts';
import { fileToBuffer } from '../lib/file-to-buffer';
import { loadImg } from '../lib/load-img';
import { ConversionProgressReport, ConversionResponse } from './convert-raw-preview.service';
import { MimeTypeService } from './mime-type.service';

@Injectable({
  providedIn: 'root',
})
export class ConvertWebImageService {
  private mimeTypeService = inject(MimeTypeService);

  private toConvert: {
    file: File;
    observer: Subscriber<ConversionResponse | ConversionProgressReport>;
    toFormat: string;
    quality: number;
  }[] = [];

  private convert$ = new Subject<void>();

  private convertFiles$ = this.convert$.subscribe(() => {
    const conversion = this.toConvert.shift();
    if (!conversion) return;
    const { file, observer, toFormat, quality } = conversion;

    observer.next({ status: 'processing' });
    console.debug('ConvertWebImageService converting', file, toFormat, quality);
    from(fileToBuffer(file))
      .pipe(
        switchMap(buffer => {
          const src = URL.createObjectURL(new Blob([buffer]));
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          return from(loadImg(src)).pipe(
            switchMap(img => {
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              ctx.drawImage(img, 0, 0);
              return new Observable<{ src: string; size: number; width: number; height: number }>(_observer => {
                canvas.toBlob(
                  blob => {
                    try {
                      URL.revokeObjectURL(src);
                      const url = URL.createObjectURL(blob);
                      _observer.next({ src: url, size: blob.size, width: canvas.width, height: canvas.height });
                      _observer.complete();
                    } catch {
                      _observer.error('ConvertWebImageService error');
                      _observer.complete();
                    }
                  },
                  this.mimeTypeService.getWebImageMimeType(toFormat),
                  quality
                );
              });
            })
          );
        }),
        map(({ src, size, width, height }) => {
          return {
            src,
            file,
            outputName: this.getOutputName(file, toFormat),
            outputSize: size,
            outputWidth: width,
            outputHeight: height,
            targetFormat: toFormat,
          };
        }),
        catchError(err => {
          console.error('ConvertWebImageService error', err);
          return of({ error: err, file, targetFormat: toFormat });
        }),
        tap(() => this.convert$.next())
      )
      .subscribe(observer);
  });

  convertFile(
    file: File,
    toFormat: string,
    quality: number
  ): Observable<ConversionResponse | ConversionProgressReport> {
    return new Observable((observer: Subscriber<ConversionResponse | ConversionProgressReport>) => {
      this.toConvert.push({ file, observer, toFormat, quality });
      this.convert$.next();
    });
  }

  canConvertFile(file: File, targetFormat: string) {
    const extension = file.name.split('.').pop().toLowerCase();
    return [extension, targetFormat].every(format => browserRenderableExtensions.includes(format));
  }

  private getOutputName(file: File, toFormat: string) {
    const name = file.name.split('.')[0];
    return name + '.' + toFormat;
  }
}
